/**
 * Заведение культур в вариант плана — «что и где сеем в плановом сезоне».
 *
 * Почему культура заводится ПО ПОЛЯМ, а не «культура на 400 га»
 * -------------------------------------------------------------
 * Строка плана в системе не хранится отдельной записью: экран назначения
 * техкарт собирает её из посевов варианта группировкой «культура ×
 * предшественник». Предшественник берётся с поля (факт прошлого сезона),
 * площадь — с контура поля. Строка «культура на N га без полей» не дала бы ни
 * того, ни другого: предшественник неизвестен, площадь введена рукой и живёт
 * своей жизнью рядом с земельным банком. Поэтому здесь — выбор полей, а
 * площадь и предшественник приезжают сами.
 *
 * Язык — агронома: «завести культуру в план», «поле занято», «убрать культуру
 * из плана». Слов scenario / sowing / row наружу не отдаём.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  applyYieldSummary,
  type ApplyYieldResult,
  type YieldGapCrop,
} from '@/lib/planning/applyYieldDefaults';
import { cropStructureKeys, planVariantKeys, techCardKeys } from '@/lib/planning/queryKeys';
import { techCardErrorText } from '@/lib/planning/techCards';
import { toast, type ToastKind, type ToastOptions } from '@/lib/toast';

export interface VariantField {
  id: string;
  name: string;
  area_ha: number;
  predecessor_crop_id: string | null;
  predecessor_name: string | null;
  /**
   * ПРЕДпредшественник — факт позапрошлого сезона. Возврат культуры через год
   * («подсолнечник — пшеница — подсолнечник») по одному прошлому году не
   * виден, поэтому агроном читает историю парой.
   */
  predecessor2_crop_id: string | null;
  predecessor2_name: string | null;
  /** Годы сезонов-источников: ими подписаны колонки («факт 2026»). */
  predecessor_year: number | null;
  predecessor2_year: number | null;
  /**
   * История занятий поля по годам, от СТАРОГО к свежему (AK-767).
   *
   * Строка приезжает на каждый год глубины, даже пустой: иначе колонки-годы
   * разъехались бы от строки к строке, и «2026» одного поля встало бы под
   * «2024» соседнего. Последний элемент — тот же сезон, что `predecessor_*`.
   */
  history: FieldHistoryYear[];
  /** Чем поле занято В ЭТОМ варианте: культура, пар или ничего. */
  occupied_by: string | null;
  /** Занятие сидит на зонах поля — из этого диалога такое поле не трогаем. */
  occupied_by_subzones: boolean;
  /**
   * Культура плана этого варианта — машиночитаемо, в отличие от `occupied_by`.
   * Пусто и когда поле свободно, и когда оно под паром: пар — состояние поля,
   * а не культура, и техкарту с ценой на него не повесить.
   */
  plan_crop_id: string | null;
  plan_crop_name: string | null;
  /** Плановая урожайность посева, ц/га. */
  planned_yield_c_ha: number | null;
  /** Сам плановый посев — по нему правятся сорт и урожайность строки. */
  plan_sowing_id: string | null;
  /** Сорт/гибрид планового посева — главный (смесь по контурам живёт в карточке поля). */
  plan_variety_id: string | null;
  plan_variety_name: string | null;
  /**
   * Правило севооборота, которым культура попала на поле (AK-767).
   * `null` — поставлено рукой. Из самих данных эти два случая неразличимы,
   * поэтому признак приезжает с сервера, а не выводится на экране.
   */
  rotation_rule_id: string | null;
  /**
   * Происхождение строки плана (ADR-075): `manual` — поставил человек,
   * `rules` — правила севооборота, `auto` — автосбор по целям, `import` —
   * загрузка из файла. Пусто только у НЕЗАНЯТОГО поля: у занятой строки
   * неизвестное происхождение сервер отдаёт как `manual` — то, чей автор
   * неизвестен, автоматика не переписывает.
   */
  plan_row_origin: PlanRowOrigin | null;
  /** Норматив урожайности культуры (Р2) — то, что поле унаследовало бы. */
  yield_default_c_ha: number | null;
  /**
   * Откуда взялась цифра урожайности на поле. `inherited` — совпадает с
   * нормативом, `overridden` — на поле своя, `explicit` — норматива нет,
   * а цифра есть, `none` — урожайности нет.
   */
  yield_source: YieldSource;
}

export type YieldSource = 'inherited' | 'overridden' | 'explicit' | 'none';

/** Кто поставил культуру в строку плана — под пометку происхождения (AK-791). */
export type PlanRowOrigin = 'manual' | 'rules' | 'auto' | 'import';

/** Чем поле было занято в одном прошлом сезоне. */
export interface FieldHistoryYear {
  year: number;
  crop_id: string | null;
  /** Культура, подтип пара («Пар чистый») либо `null` — поля не было в обороте. */
  name: string | null;
  is_fallow: boolean;
}

export interface SkippedField {
  field_id: string;
  name: string;
  reason: string;
}

export interface AddCropResult {
  created: number;
  area_ha: number;
  skipped: SkippedField[];
  /** Сколько полей сменили культуру (режим замены) — новость, отличная от «посеяно». */
  replaced?: number;
}

export const planCropKeys = {
  fields: (scenarioId: string | null) =>
    ['planning', 'plan-variant', 'fields', scenarioId ?? 'none'] as const,
};

/** Пашня хозяйства глазами варианта: что свободно, что уже занято. */
export function useVariantFields(scenarioId: string | null, enabled = true) {
  return useQuery<VariantField[], Error>({
    queryKey: planCropKeys.fields(scenarioId),
    queryFn: () => api.get<VariantField[]>(`/api/v1/planning/scenarios/${scenarioId}/fields`),
    enabled: !!scenarioId && enabled,
    staleTime: 15_000,
  });
}

/**
 * Правка структуры трогает ТРИ неймспейса: строки варианта, покрытие техкарт и
 * срез СПП. Инвалидируем все — иначе кнопка сработала, а соседний экран
 * показывает прежнюю структуру (дефект, за который #803 ловили на ревью).
 *
 * Возвращает промис, который разрешается, когда АКТИВНЫЕ запросы этих
 * неймспейсов перечитаны (`invalidateQueries` ждёт refetch). Массовые действия
 * ждут его ПЕРЕД итогом словами (AK-721): «заполнено полей 22» не имеет права
 * появиться раньше, чем таблица показала эти 22 поля.
 */
function useInvalidateStructure() {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: planVariantKeys.all() }),
      qc.invalidateQueries({ queryKey: techCardKeys.all() }),
      qc.invalidateQueries({ queryKey: cropStructureKeys.all() }),
      // Подбор по севообороту живёт в своём неймспейсе, и инвалидация плана его
      // не покрывает. Без этой строки агроном, применивший подбор и вернувшийся
      // к нему в пределах `staleTime`, увидел бы ПРЕЖНЮЮ раскладку — с полями,
      // которые он уже засеял (замечание head-web на #820).
      qc.invalidateQueries({ queryKey: rotationMatchKeys.all() }),
    ]);
  };
}

/**
 * Итог действия словами — в правильном месте (AK-721).
 *
 * Массовое действие (несколько полей, весь вариант) отчитывается карточкой по
 * ЦЕНТРУ экрана: это ответ на одно намеренное нажатие над сотнями строк, и
 * человек ждёт его там, куда смотрит. Правка одного поля из строки — обычное
 * уведомление в стопке: центрировать «Поле 12: посеяно» на каждый клик —
 * усталость, а не обратная связь.
 *
 * Звать ТОЛЬКО после `await invalidate()` — момент показа и есть суть фикса.
 */
const BULK_RESULT: ToastOptions = { placement: 'center' };

function report(kind: ToastKind, text: string, bulk: boolean): void {
  // Через `toast[kind]`, а не `toastBulkResult`: тесты экранов мокают модуль
  // `@/lib/toast` одним объектом `toast` и считают итоги по нему.
  toast[kind](text, bulk ? BULK_RESULT : undefined);
}

export function useAddCropToVariant() {
  const invalidate = useInvalidateStructure();
  return useMutation<
    AddCropResult,
    Error,
    {
      scenarioId: string;
      cropId: string;
      fieldIds: string[];
      plannedYieldCHa: number | null;
      cropName: string;
      /**
       * «Сменить культуру», а не «занять свободное поле». Ставим его только
       * там, где агроном ЯВНО метит в занятые поля (правка строки посевного
       * плана): в массовом заведении молчаливая перезапись чужой работы —
       * последнее, чего он ждёт от кнопки «завести культуру».
       */
      replace?: boolean;
    }
  >({
    mutationFn: ({ scenarioId, cropId, fieldIds, plannedYieldCHa, replace }) =>
      api.post<AddCropResult>(`/api/v1/planning/scenarios/${scenarioId}/rows`, {
        crop_id: cropId,
        field_ids: fieldIds,
        planned_yield_c_ha: plannedYieldCHa,
        ...(replace ? { replace: true } : {}),
      }),
    // Инвалидация в `onSettled`: даже отказ ручки не гарантирует, что на
    // сервере ничего не изменилось (таймаут после записи), и экран обязан
    // перечитать факт независимо от исхода (правило «Массовые действия», п.2).
    // Итог — ПОСЛЕ перечитки (п.5, AK-721): иначе «посеяно 12» читается
    // раньше, чем таблица показала эти поля.
    onSettled: async (res, _err, vars) => {
      await invalidate();
      if (!res) return;
      report(
        res.created === 0 ? 'warning' : 'success',
        addCropSummary(res, vars.cropName),
        vars.fieldIds.length > 1,
      );
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/**
 * Подбор по севообороту: где поля стоят на кольце и что кольцо предлагает.
 *
 * Ручка ЧИТАЮЩАЯ — кольцо крутят сколько угодно, план не меняется, пока не
 * нажали «применить». Кольцо едет в адресе запроса и нигде не хранится: в этой
 * версии это разовая операция подбора (см. PR/бэк).
 */
export const rotationMatchKeys = {
  all: () => ['planning', 'rotation-match'] as const,
  match: (scenarioId: string | null, ring: readonly string[]) =>
    ['planning', 'rotation-match', scenarioId ?? 'none', ring.join('>')] as const,
};

export function useRotationMatch<T>(scenarioId: string | null, ring: readonly string[]) {
  const query = ring.map((r) => `ring=${encodeURIComponent(r)}`).join('&');
  return useQuery<T, Error>({
    queryKey: rotationMatchKeys.match(scenarioId, ring),
    queryFn: () =>
      api.get<T>(`/api/v1/planning/scenarios/${scenarioId}/rotation-match?${query}`),
    enabled: !!scenarioId && ring.length > 0,
    staleTime: 30_000,
  });
}

/** Сорт/гибрид из справочника сортов конкретной культуры. */
export interface VarietyOption {
  id: string;
  name: string;
  is_hybrid: boolean;
}

interface VarietyApiItem {
  id: string;
  crop_id: string;
  name_translations?: Record<string, string> | null;
  name?: string | null;
  is_hybrid?: boolean | null;
}

/**
 * Сорта выбранной культуры — для выпадашки в строке плана.
 *
 * Справочник спрашиваем ПО КУЛЬТУРЕ: показывать агроному сорта подсолнечника
 * под пшеницей — верный способ получить посев, который не сойдётся ни с
 * техкартой, ни с нормой высева. Сортировка алфавитная, поиск по названию —
 * сквозной паттерн выпадашек-справочников.
 */
export function useCropVarieties(cropId: string | null) {
  return useQuery<VarietyOption[], Error>({
    queryKey: ['reference', 'varieties', cropId ?? 'none'] as const,
    queryFn: async () => {
      const res = await api.get<{ items: VarietyApiItem[] }>(
        `/api/v1/reference/varieties?crop_id=${cropId}&limit=500`,
      );
      return (res.items ?? [])
        .map((v) => ({
          id: v.id,
          name: v.name_translations?.ru || v.name || 'сорт без названия',
          is_hybrid: v.is_hybrid === true,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    },
    enabled: !!cropId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Массовое заведение культуры пачками — движок автозаполнения (этап 2).
 *
 * Каждая пачка уходит ОДНИМ `POST .../rows` со списком полей, пачек столько,
 * сколько разных культур (обычно три-пять). Идём последовательно и с одной
 * инвалидацией в конце — по той же причине, что и массовое снятие: иначе
 * общий отказ дал бы серию уведомлений вместо одного итога.
 *
 * `replace` НЕ ставим сознательно: автозаполнение только добавляет и не
 * трогает уже назначенное. Отбор свободных полей делает экран, но и бэк на
 * этом стоит вторым рубежом — занятое поле вернётся в `skipped`.
 */
export interface FillBatchInput {
  cropId: string;
  cropName: string;
  fieldIds: string[];
}

export interface FillResult {
  created: number;
  areaHa: number;
  /** Культур, по которым пачка прошла. */
  appliedCrops: number;
  skipped: SkippedField[];
  /** Культуры, на которых пачка не прошла, — с причиной. */
  failed: { cropName: string; reason: string }[];
}

export function useFillPlanBatches() {
  const invalidate = useInvalidateStructure();
  return useMutation<
    FillResult,
    Error,
    { scenarioId: string; batches: FillBatchInput[]; source: string }
  >({
    mutationFn: async ({ scenarioId, batches }) => {
      const result: FillResult = {
        created: 0,
        areaHa: 0,
        appliedCrops: 0,
        skipped: [],
        failed: [],
      };
      for (const batch of batches) {
        try {
          const res = await api.post<AddCropResult>(
            `/api/v1/planning/scenarios/${scenarioId}/rows`,
            {
              crop_id: batch.cropId,
              field_ids: batch.fieldIds,
              planned_yield_c_ha: null,
            },
          );
          result.created += res.created;
          result.areaHa += res.area_ha;
          result.appliedCrops += 1;
          result.skipped.push(...(res.skipped ?? []));
        } catch (err) {
          // Отказ пачки НЕ бросаем наружу: часть культур уже засеяна на
          // сервере, и если оборвать серию исключением, экран останется со
          // старыми цифрами, а итог промолчит о сделанном. Копим причину и
          // идём дальше — ровно как в массовом снятии (замечание head-web).
          result.failed.push({
            cropName: batch.cropName,
            reason: techCardErrorText(err instanceof Error ? err : new Error(String(err))),
          });
        }
      }
      return result;
    },
    // Инвалидация в `onSettled`, а не в `onSuccess`: план мог измениться и при
    // частичном отказе, и экран обязан показать ФАКТ независимо от исхода.
    // Итог — только когда таблица уже перечитана (AK-721: founder читал
    // «заполнено 22», а в таблице ещё стояло «культура не назначена»).
    onSettled: async (res, _err, vars) => {
      await invalidate();
      if (!res) return;
      const text = fillBatchesSummary(res, vars.source);
      // Ничего не легло, и были отказы — это неудача, и звучать должна так.
      const kind: ToastKind =
        res.created === 0 && res.failed.length > 0
          ? 'error'
          : res.created === 0
            ? 'warning'
            : 'success';
      report(kind, text, true);
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/**
 * Итог автозаполнения — ОДИН на всё действие, включая неуспех.
 *
 * Массовое действие обязано вести себя как одно действие и при отказе тоже:
 * человек должен узнать, что успело примениться, а не только про ошибку.
 */
export function fillBatchesSummary(res: FillResult, source: string): string {
  const parts: string[] = [];
  parts.push(
    res.created > 0
      ? `${source}: заполнено полей ${res.created} · ${HA.format(Math.round(res.areaHa))} га`
      : `${source}: ни одно поле не заполнилось`,
  );
  if (res.skipped.length > 0) {
    parts.push(`пропущено ${res.skipped.length} — ${res.skipped[0].reason}`);
  }
  if (res.failed.length > 0) {
    const names = res.failed.map((f) => f.cropName).slice(0, 2).join(', ');
    const tail = res.failed.length > 2 ? ` и ещё ${res.failed.length - 2}` : '';
    parts.push(`не удалось на культуре ${names}${tail} — ${res.failed[0].reason}`);
  }
  return `${parts[0]}${parts.length > 1 ? `. ${parts.slice(1).join(', ')}` : ''}`;
}

/**
 * «Подтянуть нормативы урожайности» к уже разложенным строкам плана (AK-778).
 *
 * Одна ручка на всё действие: `crop_id: null` — весь вариант, `crop_id` —
 * только эта культура. Трогаются ТОЛЬКО пустые урожайности; своя цифра
 * переживает применение, и итог обязан сказать об этом словами — иначе
 * действие читается как разрушительное и им перестают пользоваться.
 *
 * Инвалидация — в `onSettled` и одна на действие (правило «Массовые
 * действия», п. 2): ручка правит сотни строк разом, и даже её отказ не
 * гарантирует, что на сервере ничего не записалось.
 */
export function useApplyYieldDefaults() {
  const invalidate = useInvalidateStructure();
  return useMutation<
    ApplyYieldResult,
    Error,
    {
      scenarioId: string;
      /** `null` — весь вариант плана. */
      cropId: string | null;
      /** Культуры без норматива на момент нажатия — для итога поимённо. */
      gapsWithoutDefault: YieldGapCrop[];
    }
  >({
    mutationFn: ({ scenarioId, cropId }) =>
      api.post<ApplyYieldResult>(
        `/api/v1/planning/scenarios/${scenarioId}/rows/apply-yield-defaults`,
        { crop_id: cropId },
      ),
    // Итог — после перечитки строк (AK-721), карточкой по центру.
    onSettled: async (res, _err, vars) => {
      await invalidate();
      if (!res) return;
      const text = applyYieldSummary(res, vars.gapsWithoutDefault);
      // Ноль заполненных — это не успех: человек нажал и ничего не получил,
      // и сказать об этом надо тоном «внимание», а не зелёной галочкой.
      report(res.updated === 0 ? 'warning' : 'success', text, true);
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

export interface FieldSowingPatched {
  sowing_id: string;
  planned_yield_c_ha: number | null;
  variety_id: string | null;
  variety_name: string | null;
}

/**
 * Правка сорта и плановой урожайности прямо в строке.
 *
 * `set_*`-флаги отделяют «не трогай» от «сотри»: без них снять ошибочно
 * выбранный сорт можно было бы только вместе с урожайностью, а `null` в теле
 * читался бы как «поле не прислали».
 */
export function usePatchFieldSowing() {
  const invalidate = useInvalidateStructure();
  return useMutation<
    FieldSowingPatched,
    Error,
    {
      scenarioId: string;
      fieldId: string;
      fieldName: string;
      varietyId?: string | null;
      plannedYieldCHa?: number | null;
      setVariety?: boolean;
      setYield?: boolean;
    }
  >({
    mutationFn: ({ scenarioId, fieldId, varietyId, plannedYieldCHa, setVariety, setYield }) =>
      api.patch<FieldSowingPatched>(
        `/api/v1/planning/scenarios/${scenarioId}/fields/${fieldId}/sowing`,
        {
          ...(setVariety ? { set_variety: true, variety_id: varietyId ?? null } : {}),
          ...(setYield ? { set_yield: true, planned_yield_c_ha: plannedYieldCHa ?? null } : {}),
        },
      ),
    onSuccess: (res, vars) => {
      void invalidate();
      toast.success(fieldSowingPatchSummary(res, vars.fieldName));
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/** Итог правки словами: что именно изменилось на поле. */
export function fieldSowingPatchSummary(res: FieldSowingPatched, fieldName: string): string {
  const parts: string[] = [];
  if (res.variety_name) parts.push(`сорт «${res.variety_name}»`);
  if (res.planned_yield_c_ha !== null) parts.push(`урожайность ${res.planned_yield_c_ha} ц/га`);
  return parts.length > 0
    ? `${fieldName}: ${parts.join(', ')}`
    : `${fieldName}: сорт и урожайность не заданы`;
}

/** Поле, с которого снимаем культуру: id для запроса, имя и га — для итога. */
export interface ClearTarget {
  id: string;
  name: string;
  area_ha: number;
  cropName: string | null;
}

export interface ClearFieldsResult {
  /** Полей, где посев действительно сняли. */
  cleared: number;
  areaHa: number;
  /** Полей, где снимать было нечего (уже свободны) — это не ошибка. */
  alreadyFree: number;
  failed: { name: string; reason: string }[];
}

/**
 * «Снять культуру» — освободить одно или несколько полей варианта.
 *
 * Соседняя `useRemoveCropFromVariant` убирает культуру со ВСЕХ полей сразу:
 * она обслуживает строку «культура по предшественнику». В посевном плане
 * агроном работает по полям, и «убери вот с этих» не должно означать «убери
 * со ста сорока».
 *
 * Почему цикл, а не `forEach` с параллельными запросами (замечание head-web
 * на #817). Массовое снятие обязано вести себя как ОДНО действие:
 *   • параллельный веер на сотне выбранных полей — сотня запросов разом;
 *   • каждый успех дёргал бы инвалидацию трёх неймспейсов — сотня×3;
 *   • и главное: один общий отказ (нет прав, заперт сезон, обрыв сети) дал бы
 *     сотню уведомлений об ошибке подряд, которые человек закрывает по одному.
 * Поэтому: последовательно, отказ каждого поля копится в результат, ОДНА
 * инвалидация в конце и ОДИН итог словами — тем же приёмом, что `addCropSummary`
 * у назначения. Назначение уходит одним POST со списком полей, снятие —
 * последовательной серией: bulk-ручки снятия у бэка нет, и для v1 это
 * достаточно (решение подтверждено head-web).
 */
export function useClearFieldsInVariant() {
  const invalidate = useInvalidateStructure();
  return useMutation<
    ClearFieldsResult,
    Error,
    { scenarioId: string; fields: ClearTarget[] }
  >({
    mutationFn: async ({ scenarioId, fields }) => {
      const result: ClearFieldsResult = {
        cleared: 0,
        areaHa: 0,
        alreadyFree: 0,
        failed: [],
      };
      for (const field of fields) {
        try {
          const res = await api.delete<{ removed: number }>(
            `/api/v1/planning/scenarios/${scenarioId}/fields/${field.id}/sowing`,
          );
          if (res.removed > 0) {
            result.cleared += 1;
            result.areaHa += field.area_ha;
          } else {
            result.alreadyFree += 1;
          }
        } catch (err) {
          // Отказ одного поля не должен ронять остальные: агроном выбрал
          // двадцать, на одном чужая зона — правильный ответ «девятнадцать
          // сняли, вот это не вышло», а не «ничего не получилось».
          result.failed.push({
            name: field.name,
            reason: techCardErrorText(err instanceof Error ? err : new Error(String(err))),
          });
        }
      }
      return result;
    },
    // Серия могла снять часть полей и упасть посередине — экран обязан
    // показать факт при любом исходе, поэтому инвалидация живёт в `onSettled`
    // (правило «Массовые действия», п.2), а не в `onSuccess`. Итог — после
    // перечитки (п.5, AK-721).
    onSettled: async (res, _err, vars) => {
      await invalidate();
      if (!res) return;
      const text = clearFieldsSummary(res, vars.fields);
      // Ни одного успеха — это неудача целиком, и говорить о ней надо как о
      // неудаче. Частичный успех остаётся успехом с оговоркой.
      report(
        res.cleared === 0 && res.failed.length > 0 ? 'error' : 'success',
        text,
        vars.fields.length > 1,
      );
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/**
 * Итог снятия словами — ОДИН на всё действие, сколько бы полей ни выбрали.
 * Симметрично `addCropSummary`: два массовых действия на одном экране должны
 * отчитываться одинаково.
 */
export function clearFieldsSummary(
  res: ClearFieldsResult,
  fields: readonly { name: string; cropName?: string | null }[],
): string {
  const single = fields.length === 1 ? fields[0] : null;
  if (single) {
    if (res.failed.length > 0) return `${single.name}: ${res.failed[0].reason}`;
    if (res.cleared === 0) return `${single.name}: снимать было нечего — поле и так свободно`;
    const crop = single.cropName ? `${single.cropName} снята` : 'культура снята';
    return `${single.name}: ${crop} — поле свободно в этом варианте`;
  }

  const parts: string[] = [];
  if (res.cleared > 0) {
    parts.push(`Культура снята с полей: ${res.cleared} · ${HA.format(Math.round(res.areaHa))} га`);
  } else {
    parts.push('Ни одно поле не освободилось');
  }
  if (res.alreadyFree > 0) parts.push(`уже были свободны: ${res.alreadyFree}`);
  if (res.failed.length > 0) {
    const names = res.failed.slice(0, 3).map((f) => f.name).join(', ');
    const tail = res.failed.length > 3 ? ` и ещё ${res.failed.length - 3}` : '';
    parts.push(`не удалось на ${res.failed.length} (${names}${tail}) — ${res.failed[0].reason}`);
  }
  return `${parts[0]}${parts.length > 1 ? `. ${parts.slice(1).join(', ')}` : ''}`;
}

export interface FieldHistoryResult {
  created: number;
  season_year: number;
  skipped: SkippedField[];
}

/**
 * «Нет данных → указать»: ручной факт истории поля из посевного плана (AK-694).
 *
 * Пишется ОБЫЧНЫЙ факт-посев прошлого сезона (`scenario_id IS NULL`) — сезон
 * задан ГОДОМ КОЛОНКИ, по которой кликнули, и не вычисляется заново. Ответ
 * приходит с накопленными отказами (`skipped`), и это одно действие с одним
 * итогом — сколько бы полей ни выбрали (правило «Массовые действия»).
 */
export function useAddFieldHistory() {
  const invalidate = useInvalidateStructure();
  return useMutation<
    FieldHistoryResult,
    Error,
    {
      scenarioId: string;
      seasonYear: number;
      cropId: string;
      /** Имя культуры — для итога словами, не идентификатором. */
      cropName: string;
      fieldIds: string[];
      /** Площадь рукой — только для одиночной записи; null = по контуру. */
      areaHa: number | null;
    }
  >({
    mutationFn: ({ scenarioId, seasonYear, cropId, fieldIds, areaHa }) =>
      api.post<FieldHistoryResult>(
        `/api/v1/planning/scenarios/${scenarioId}/fields/history`,
        {
          season_year: seasonYear,
          crop_id: cropId,
          field_ids: fieldIds,
          area_ha: areaHa,
        },
      ),
    // Даже отказ ручки не гарантирует, что на сервере ничего не записалось
    // (таймаут после записи) — экран перечитывает факт при любом исходе
    // (правило «Массовые действия», п.2). Итог — после перечитки (п.5, AK-721).
    onSettled: async (res, _err, vars) => {
      await invalidate();
      if (!res) return;
      const text = fieldHistorySummary(res, vars.cropName);
      report(
        res.created === 0 && res.skipped.length > 0 ? 'warning' : 'success',
        text,
        vars.fieldIds.length > 1,
      );
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

/** Итог записи истории — ОДИН на всё действие, с именами и причинами. */
export function fieldHistorySummary(res: FieldHistoryResult, cropName: string): string {
  const head =
    res.created > 0
      ? `История записана: «${cropName}» в сезоне ${res.season_year} · полей: ${res.created}`
      : `История не записана: «${cropName}» в сезоне ${res.season_year}`;
  if (res.skipped.length === 0) return head;
  const names = res.skipped.slice(0, 3).map((s) => s.name).join(', ');
  const tail = res.skipped.length > 3 ? ` и ещё ${res.skipped.length - 3}` : '';
  return `${head}. Пропущено полей ${res.skipped.length}: ${names}${tail} — ${res.skipped[0].reason}`;
}

export function useRemoveCropFromVariant() {
  const invalidate = useInvalidateStructure();
  return useMutation<
    { removed: number },
    Error,
    { scenarioId: string; cropId: string; predecessorCropId?: string | null; cropName: string }
  >({
    mutationFn: ({ scenarioId, cropId, predecessorCropId }) =>
      api.delete<{ removed: number }>(
        `/api/v1/planning/scenarios/${scenarioId}/rows?crop_id=${cropId}` +
          (predecessorCropId ? `&predecessor_crop_id=${predecessorCropId}` : ''),
      ),
    // Снятие культуры со ВСЕХ её полей — массовое действие: инвалидация в
    // `onSettled`, итог по центру и после перечитки (AK-721).
    onSettled: async (res, _err, vars) => {
      await invalidate();
      if (!res) return;
      report(
        'success',
        res.removed > 0
          ? `${vars.cropName} убрана из плана — освободилось полей: ${res.removed}`
          : 'Убирать было нечего: в этом варианте культура не заведена',
        true,
      );
    },
    onError: (err) => toast.error(techCardErrorText(err)),
  });
}

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/** Итог заведения словами: сколько посеяли и почему часть полей пропущена. */
export function addCropSummary(res: AddCropResult, cropName: string): string {
  // Замена — отдельная новость: «посеяно 12 полей» там, где 12 полей ПОТЕРЯЛИ
  // прежнюю культуру, звучит безобиднее, чем есть на самом деле.
  const replacedTail = res.replaced ? `, из них сменили культуру ${res.replaced}` : '';
  const head =
    res.created > 0
      ? `${cropName}: посеяно полей ${res.created} на ${HA.format(Math.round(res.area_ha))} га${replacedTail}`
      : `${cropName}: ни одного поля не посеяно`;
  if (res.skipped.length === 0) return head;
  const names = res.skipped.slice(0, 3).map((s) => s.name).join(', ');
  const tail = res.skipped.length > 3 ? ` и ещё ${res.skipped.length - 3}` : '';
  return `${head}. Пропущено полей ${res.skipped.length}: ${names}${tail} — ${res.skipped[0].reason}`;
}

/** Свободные поля наверху: агроном заводит культуру именно на них. */
export function sortFieldsForPicker(fields: readonly VariantField[]): VariantField[] {
  return [...fields].sort((a, b) => {
    const aFree = !a.occupied_by && !a.occupied_by_subzones;
    const bFree = !b.occupied_by && !b.occupied_by_subzones;
    if (aFree !== bFree) return aFree ? -1 : 1;
    return a.name.localeCompare(b.name, 'ru');
  });
}

export function totalAreaOf(fields: readonly VariantField[], selected: ReadonlySet<string>): number {
  return fields.reduce((sum, f) => (selected.has(f.id) ? sum + f.area_ha : sum), 0);
}
